import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, X, Sparkles, Clock } from "lucide-react";

const API = `${window.location.origin}/api`;
const DISMISS_KEY = "upgradeBannerDismissedAt";

/**
 * Banner discreto exibido SOMENTE no Início (HomePage) para usuários
 * que estão no plano Start ou cujo trial está acabando. Pode ser fechado;
 * volta a aparecer após 24h.
 */
export default function UpgradeBanner() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const dismiss = localStorage.getItem(DISMISS_KEY);
        if (dismiss) {
          const horas = (Date.now() - parseInt(dismiss, 10)) / 3600000;
          if (horas < 24) return;
        }
        const { data } = await axios.get(`${API}/planos/meu`);
        if (data?.super_admin) return;
        const planoId = data?.plano?.id;
        const trial = data?.trial;
        // Mostrar se está no Start ou se trial acabando (<=3 dias)
        if (planoId === "start") {
          setInfo({ tipo: "start" });
          setVisible(true);
        } else if (trial?.ativo && trial.dias_restantes <= 3) {
          setInfo({
            tipo: "trial_expirando",
            dias: trial.dias_restantes,
            planoEscolhido: trial.plano_escolhido,
          });
          setVisible(true);
        } else if (trial && !trial.ativo && data?.subscription_status === "trial") {
          // Trial expirado, sem pagamento
          setInfo({ tipo: "trial_expirado", planoEscolhido: trial.plano_escolhido });
          setVisible(true);
        }
      } catch (e) {
        /* silencioso */
      }
    })();
  }, []);

  const fechar = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible || !info) return null;

  let conteudo = null;
  if (info.tipo === "start") {
    conteudo = {
      icon: Sparkles,
      titulo: "Seu plano é Start — desbloqueie o crescimento",
      sub: "Mais carros, Secretário, Pós-Venda automatizado e Contas a Pagar PF/PJ.",
      cor: "from-blue-500 to-emerald-500",
      btn: "Ver planos",
    };
  } else if (info.tipo === "trial_expirando") {
    conteudo = {
      icon: Clock,
      titulo: `⏰ ${info.dias} dia(s) de trial restantes`,
      sub: `Seu acesso ao plano ${info.planoEscolhido?.toUpperCase()} termina em breve. Garanta sua assinatura para não perder os recursos premium.`,
      cor: "from-amber-500 to-orange-500",
      btn: "Assinar agora",
    };
  } else if (info.tipo === "trial_expirado") {
    conteudo = {
      icon: Clock,
      titulo: "Seu trial terminou",
      sub: `Você foi movido para o plano Start. Assine ${info.planoEscolhido?.toUpperCase()} para reativar todos os recursos.`,
      cor: "from-red-500 to-orange-500",
      btn: "Reativar agora",
    };
  }

  const Icon = conteudo.icon;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl mb-6 p-4 sm:p-5 bg-gradient-to-r ${conteudo.cor} text-white shadow-lg`}
      data-testid="upgrade-banner"
    >
      <button
        onClick={fechar}
        className="absolute top-2 right-2 text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
        aria-label="Fechar"
        data-testid="upgrade-banner-close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pr-6">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base sm:text-lg" style={{ fontFamily: "Work Sans" }}>
            {conteudo.titulo}
          </p>
          <p className="text-sm text-white/90 mt-0.5">{conteudo.sub}</p>
        </div>
        <button
          onClick={() => navigate("/planos")}
          className="bg-white text-gray-900 hover:bg-gray-100 font-bold px-4 py-2 rounded-lg gap-2 inline-flex items-center text-sm shrink-0"
          data-testid="upgrade-banner-cta"
        >
          <Crown className="h-4 w-4 text-amber-500" />
          {conteudo.btn}
        </button>
      </div>
    </div>
  );
}
